import { armOf, MENSCH_SEATS, START_SPACING, TRACK_LEN } from "@bing/engine-mensch";

/* ------------------------------------------------------------------ *
 * The منچ board GEOMETRY, as pure data.
 *
 * The board is an 11×11 grid holding a cross whose arms are three cells
 * wide. That grid is not arbitrary — it is the only one that yields the
 * real Mensch numbers:
 *
 *   cross cells      11×3 + 11×3 − 3×3 = 57
 *   − home cells     4 seats × 4       = 16
 *   − centre         (5,5), decorative =  1
 *   ------------------------------------------
 *   track squares                      = 40   ✔
 *
 * The middle lane of each arm is that seat's home run; the two outer
 * lanes carry the shared track. One quadrant is ten cells, and the next
 * is simply that quadrant rotated a quarter turn — which is exactly why
 * the four start squares sit 10 apart.
 *
 * Grid coordinates are (col, row), both 0..10, origin top-left.
 * ------------------------------------------------------------------ */

/** The board grid is 11×11 cells. */
export const GRID = 11;
/** The four board arms, always all painted — an empty one is decoration. */
export const MENSCH_ARMS = [0, 1, 2, 3] as const;
/** The centre index (5) — the decorative middle the four home runs point at. */
export const MID = (GRID - 1) / 2;

export interface Cell {
  readonly col: number;
  readonly row: number;
}

/** Rotate a cell a quarter turn CLOCKWISE about the board centre. */
export function rotate90(c: Cell): Cell {
  return { col: GRID - 1 - c.row, row: c.col };
}

/** Rotate a cell `turns` quarter-turns clockwise (any integer, negatives included). */
export function rotateBy(c: Cell, turns: number): Cell {
  let out = c;
  for (let i = 0; i < ((turns % 4) + 4) % 4; i += 1) out = rotate90(out);
  return out;
}

/**
 * The arm the LOCAL player must appear on, so their own colour sits at their own side of
 * the board. Arm 3 is the near side in portrait: its yard is the bottom-left corner, its
 * start square is on the bottom edge, and its home run climbs the bottom arm — i.e. the
 * seat you would take if you sat down in front of the phone.
 */
export const NEAR_ARM = 3;

/**
 * Quarter-turns to rotate the whole board so that `you` lands on {@link NEAR_ARM}.
 *
 * The board is rotationally symmetric, so spinning the rendering is enough — no engine
 * seat is renumbered, and every other player keeps their own colour and their true
 * position relative to you.
 */
export function viewTurns(you: number, playerCount: number): number {
  return (((NEAR_ARM - armOf(you, playerCount)) % 4) + 4) % 4;
}

/**
 * Seat 0's ten track squares: out along the top lane of the left arm, up and over
 * the top-left arm tip, ending on the square above the vertical arm's middle lane.
 * Every other quadrant is this one rotated.
 */
const QUADRANT: readonly Cell[] = [
  { col: 0, row: MID - 1 }, // seat 0's START square
  { col: 1, row: MID - 1 },
  { col: 2, row: MID - 1 },
  { col: 3, row: MID - 1 },
  { col: MID - 1, row: MID - 1 }, // the inner corner of the arm
  { col: MID - 1, row: 3 },
  { col: MID - 1, row: 2 },
  { col: MID - 1, row: 1 },
  { col: MID - 1, row: 0 }, // the arm tip
  { col: MID, row: 0 }, // across the tip, onto the next arm
];

/** The 40 main-track squares in CLOCKWISE order, starting at seat 0's start square. */
export function trackCells(): Cell[] {
  const cells: Cell[] = [];
  let quadrant = QUADRANT.slice();
  for (let arm = 0; arm < MENSCH_SEATS; arm += 1) {
    cells.push(...quadrant);
    quadrant = quadrant.map(rotate90);
  }
  return cells;
}

/** The absolute track index a seat enters on — 10 apart, by construction. */
export function startIndex(seat: number, playerCount: number = MENSCH_SEATS): number {
  return armOf(seat, playerCount) * START_SPACING;
}

/**
 * A seat's four home cells, ordered from the board edge INWARD toward the centre.
 * Seat 0 runs along the middle lane of the left arm; each later seat is that run
 * rotated a quarter turn, so the four runs can never overlap.
 */
export function homeCells(seat: number, playerCount: number = MENSCH_SEATS): Cell[] {
  const run: Cell[] = [1, 2, 3, 4].map((i) => ({ col: i, row: MID }));
  return run.map((c) => rotateBy(c, armOf(seat, playerCount)));
}

/**
 * A seat's four yard cells — a 2×2 block in that seat's corner, outside the cross
 * (so it can never collide with the track or a home run).
 */
export function yardCells(seat: number, playerCount: number = MENSCH_SEATS): Cell[] {
  const block: Cell[] = [
    { col: 0, row: 0 },
    { col: 1, row: 0 },
    { col: 0, row: 1 },
    { col: 1, row: 1 },
  ];
  return block.map((c) => rotateBy(c, armOf(seat, playerCount)));
}

/**
 * Where a token sits on the grid, from its engine step.
 * `-1` → its yard slot; `0..39` → the track; `40..43` → its own home run.
 */
export function cellForStep(
  seat: number,
  step: number,
  tokenIndex: number,
  playerCount: number = MENSCH_SEATS,
): Cell {
  if (step < 0) return yardCells(seat, playerCount)[tokenIndex] ?? { col: 0, row: 0 };
  if (step >= TRACK_LEN)
    return homeCells(seat, playerCount)[step - TRACK_LEN] ?? { col: MID, row: MID };
  const track = trackCells();
  return track[(startIndex(seat, playerCount) + step) % TRACK_LEN] ?? { col: 0, row: 0 };
}

/** The four board colours, in ARM order. Kept here so board + HUD agree. */
export const SEAT_COLORS: readonly { readonly base: string; readonly light: string }[] = [
  { base: "#e9c349", light: "#ffe088" }, // gold  — arm 0
  { base: "#4ea8de", light: "#8fd3ff" }, // blue  — arm 1
  { base: "#e5564c", light: "#ff8a80" }, // red   — arm 2
  { base: "#4caf7d", light: "#8fe0b4" }, // green — arm 3
];

/**
 * ITEM 8 — the local player's chosen colour, as a ROTATION of the palette.
 *
 * You always sit at the near corner; picking a colour repaints your arm in it and shifts the
 * other three along, so no two seats can ever collide and the board still reads correctly.
 * Nothing about the seating or the geometry moves — this is presentation only.
 */
export function paletteShift(myArm: number, chosen: number): number {
  return (((chosen - myArm) % MENSCH_SEATS) + MENSCH_SEATS) % MENSCH_SEATS;
}

/**
 * A seat's colour — the colour of the ARM it sits on, NOT of its seat index.
 *
 * This matters as soon as the two differ. In a two-player game seat 1 sits on arm 2, and
 * all four arms are painted (an empty arm keeps its own colour so the board always reads
 * as a منچ board). Colouring seat 1 by its INDEX would paint its tokens blue while they
 * stand in the red arm — and blue is already arm 1's colour, sitting empty next door.
 * With four players arm === seat, so nothing changes.
 */
export function seatColor(
  seat: number,
  playerCount: number = MENSCH_SEATS,
): {
  readonly base: string;
  readonly light: string;
} {
  return SEAT_COLORS[armOf(seat, playerCount)] ?? SEAT_COLORS[0]!;
}

/** An ARM's colour, after the local player's palette choice has been applied. */
export function armColor(
  arm: number,
  shift = 0,
): { readonly base: string; readonly light: string } {
  return SEAT_COLORS[(arm + shift) % MENSCH_SEATS] ?? SEAT_COLORS[0]!;
}

/** The seat occupying `arm`, or null when that arm is decoration only. */
export function seatOnArm(arm: number, playerCount: number): number | null {
  for (let seat = 0; seat < playerCount; seat += 1)
    if (armOf(seat, playerCount) === arm) return seat;
  return null;
}

/** An ARM's four yard cells (independent of who, if anyone, is sitting there). */
export function yardCellsForArm(arm: number): Cell[] {
  const block: Cell[] = [
    { col: 0, row: 0 },
    { col: 1, row: 0 },
    { col: 0, row: 1 },
    { col: 1, row: 1 },
  ];
  return block.map((c) => rotateBy(c, arm));
}

/** An ARM's four home cells, edge → centre. */
export function homeCellsForArm(arm: number): Cell[] {
  const run: Cell[] = [1, 2, 3, 4].map((i) => ({ col: i, row: MID }));
  return run.map((c) => rotateBy(c, arm));
}

/** The track index an ARM starts on. */
export function startIndexForArm(arm: number): number {
  return arm * START_SPACING;
}
